/**
 * The Operations report's own small parts — the pieces of the published page
 * that are NOT in ds/index.tsx.
 *
 * ds/ carries the shared renderers every report page uses (fmt, Delta,
 * Sparkline, Kpi, MetricCell, TableCard, Findings, Banner). What is left over
 * is specific to this page: the threshold colour every OSA and fill-rate
 * figure is painted with, the "fixed window" caption, the segmented control,
 * the stat-list rows on Plan & forecast, and the drill-modal STACK.
 *
 * Every function below is a port of the same-named function in
 * qcomm-blinkit-warehouse/ops/report-nightly/pages/operations.html. The
 * arithmetic and the wording are copied, not reinterpreted.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';

/* ── numbers ─────────────────────────────────────────────────────────────── */

/** A finite number, or null. Everything on the page reads its figures through
 *  this: a missing value has to reach `fmt` as null so it renders an em-dash
 *  rather than NaN or a zero, which a reader cannot tell from a real zero. */
export const num = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');

/** Sum of a field over rows, null-safe. Null contributes nothing rather than
 *  poisoning the total — but a total of nothing stays null, not 0. */
export function sumOf<T>(rows: T[], pick: (r: T) => number | null): number | null {
  let any = false;
  let total = 0;
  for (const r of rows) {
    const v = pick(r);
    if (v !== null) { any = true; total += v; }
  }
  return any ? total : null;
}

/* ── threshold colour ────────────────────────────────────────────────────── */

/**
 * Port of bandColor. EVERY OSA % and fill-rate % on this page is red below 70,
 * amber 70–85 and green at or above 85, applied to the value itself and
 * independently of the colour of its delta: the delta says which way it moved,
 * the value says whether it is acceptable.
 */
export function bandColor(v: number | null, low = 70, mid = 85): string {
  if (v === null) return 'var(--ink-3)';
  return v < low ? 'var(--down)' : v < mid ? 'var(--warn)' : 'var(--up)';
}

/** Port of goodZero: a "good" zero is de-emphasised grey, so it never reads as
 *  a red 0% failure. Used for the fill-rate table's Short column. */
export function GoodZero({ value, children }: { value: number | null; children: ReactNode }) {
  if (value === 0) return <span style={{ color: 'var(--ink-3)' }}>0</span>;
  return <>{children}</>;
}

/* ── dates ───────────────────────────────────────────────────────────────── */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'] as const;

const utc = (iso: string): Date => new Date(`${iso}T00:00:00Z`);

/** Port of periodLabel: "4–10 Sep 2026" inside one month, "28 Aug – 10 Sep 2026"
 *  across two. Every scope pill on the page is this string. */
export function periodLabel(from: string | undefined, to: string | undefined): string {
  if (!from || !to) return '—';
  const a = utc(from);
  const b = utc(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return `${from} – ${to}`;
  const mo = (i: number): string => MONTHS[i]?.slice(0, 3) ?? '';
  return a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear()
    ? `${a.getUTCDate()}–${b.getUTCDate()} ${mo(b.getUTCMonth())} ${b.getUTCFullYear()}`
    : `${a.getUTCDate()} ${mo(a.getUTCMonth())} – ${b.getUTCDate()} ${mo(b.getUTCMonth())} ${b.getUTCFullYear()}`;
}

/** Inclusive day count, the same denominator the page's DRR and pro-rating use. */
export function daysBetween(a: string, b: string): number {
  return Math.round((utc(b).getTime() - utc(a).getTime()) / 864e5) + 1;
}

/** Port of monthLabel: "2026-09" -> "September 2026". */
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const name = MONTHS[(m ?? 1) - 1];
  return name && y ? `${name} ${y}` : ym;
}

/** Port of daysInMonth: day 0 of the next month is the last of this one. */
export function daysInMonth(ym: string): number {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, m ?? 1, 0)).getUTCDate();
}

/* ── the window the server resolved ──────────────────────────────────────── */

/**
 * The resolved window, WIDER than the type useReport declares.
 *
 * The API returns previous_start / previous_end / comparison_available /
 * truncated / note on every response and the shared hook's type stops at
 * {start,end,as_of}. The extras are what let this page say "vs 28 Aug – 3 Sep"
 * instead of "vs previous", and say WHY when there is no comparison — so they
 * are read here rather than dropped. Every field is optional: an older server
 * that does not send them must degrade to "no comparison", not to a crash.
 */
export interface OpsWindow {
  start: string;
  end: string;
  as_of: string;
  previous_start?: string;
  previous_end?: string;
  comparison_available?: boolean;
  truncated?: boolean;
  retention_days?: number;
  note?: string;
}

/** True only when the server says the comparison window is inside what the
 *  source holds. A delta against a period the data does not cover reads as a
 *  collapse, which is worse than no delta at all. */
export const canCompare = (w: OpsWindow | undefined): boolean =>
  w !== undefined && w.comparison_available !== false && w.previous_start !== undefined;

/** "vs 28 Aug – 3 Sep 2026" for a column header or a KPI foot. */
export const comparisonLabel = (w: OpsWindow | undefined): string =>
  canCompare(w) ? periodLabel(w?.previous_start, w?.previous_end) : '—';

/* ── the MRP / SP basis ──────────────────────────────────────────────────── */

/**
 * What the basis toggle actually changes: ONLY GMV.
 *
 * ASP is a selling price by definition and never moves. Units, DRR, OSA,
 * listing and discount never move. The plan is written on MRP, which is why
 * the plan cards warn rather than convert when the page is on SP.
 */
export type Basis = 'mrp' | 'sp';

export const basisLabel = (b: Basis): string => (b === 'mrp' ? 'MRP' : 'SP');

/* ── small parts ─────────────────────────────────────────────────────────── */

/** Port of tag(): the grey inline chip — city counts, finding types. */
export function Tag({ children, linked }: { children: ReactNode; linked?: boolean }) {
  return <span className={`tag${linked ? ' linked' : ''}`}>{children}</span>;
}

/**
 * The `.fixed-window` caption — a figure that does NOT follow the date picker,
 * or a caveat about the basis a figure sits on. On the published page it is
 * both a tag (under a sales-loss value) and a style (the plan card's caveat
 * lines); both uses are here.
 */
export function FixedWindow({ children, block }: { children: ReactNode; block?: boolean }) {
  return (
    <div className="fixed-window" {...(block ? { style: { margin: '0 18px 10px' } } : {})}>
      {children}
    </div>
  );
}

/** Port of the `.seg` segmented control. */
export function Seg<T extends string>({ value, options, onChange }: {
  value: T;
  options: Array<{ id: T; label: string }>;
  onChange: (id: T) => void;
}) {
  return (
    <div className="seg" style={{ marginLeft: 8 }}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className={o.id === value ? 'on' : ''}
          onClick={() => { onChange(o.id); }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Port of the `.period-group` toggle, which is what the report's MRP/SP
 *  control is. Kept out of ds/ because it is a page control, not a renderer. */
export function BasisToggle({ value, onChange }: { value: Basis; onChange: (b: Basis) => void }) {
  return (
    <div
      className="period-group"
      title="Plan and target are set on MRP. SP is what was realised after discount."
    >
      {(['mrp', 'sp'] as const).map((b) => (
        <button
          key={b}
          type="button"
          className={value === b ? 'on' : ''}
          onClick={() => { onChange(b); }}
        >
          {basisLabel(b)}
        </button>
      ))}
    </div>
  );
}

/** Port of the `.stat-row`: swatch, label over a sub-line, value on the right.
 *  Every swatch on Plan & forecast is `severity-low` (grey) — these rows are
 *  facts, not severities. */
export function StatRow({ label, sub, value, muted }: {
  label: string;
  sub: ReactNode;
  value: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="stat-row">
      <span className="stat-swatch severity-low" />
      <div className="stat-body">
        <div className="stat-label">{label}</div>
        <div className="stat-sub" style={{ maxWidth: 660 }}>{sub}</div>
      </div>
      <div className={`stat-val${muted ? ' muted' : ''}`}>{value}</div>
    </div>
  );
}

/** "not available" — a gap in what was pulled, deliberately not a zero. */
export function NotAvailable({ children = 'not available' }: { children?: ReactNode }) {
  return <span className="stat-val muted">{children}</span>;
}

/* ── the drill stack ─────────────────────────────────────────────────────── */

/**
 * A STACK of modals, not one modal.
 *
 * Opening a drill from inside a drill pushes the current one; closing pops
 * back to it, so a reader who went three levels deep does not lose the two
 * above. Escape pops one level, a mousedown on the scrim itself pops one, and
 * body scroll is locked while any drill is open — all four behaviours are the
 * published page's.
 *
 * Deliberately NOT a portal to document.body: the report's CSS is scoped under
 * `.rds`, which ReportShell puts on the content wrapper, so a modal rendered
 * outside that subtree would lose every style it has. `position:fixed` covers
 * the viewport from wherever it sits in the tree.
 */
interface DrillApi { push: (node: ReactNode) => void; pop: () => void; depth: number }

const DrillCtx = createContext<DrillApi>({ push: () => undefined, pop: () => undefined, depth: 0 });

export const useDrill = (): DrillApi => useContext(DrillCtx);

export function DrillHost({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<ReactNode[]>([]);

  const push = useCallback((node: ReactNode) => { setStack((s) => [...s, node]); }, []);
  const pop = useCallback(() => { setStack((s) => s.slice(0, -1)); }, []);

  useEffect(() => {
    if (stack.length === 0) return undefined;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') pop(); };
    document.addEventListener('keydown', onKey);
    // The page behind must not scroll under an open drill.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [stack.length, pop]);

  const api = useMemo<DrillApi>(() => ({ push, pop, depth: stack.length }), [push, pop, stack.length]);
  const top = stack[stack.length - 1];

  return (
    <DrillCtx.Provider value={api}>
      {children}
      {top === undefined ? null : (
        <div
          className="scrim"
          onMouseDown={(e) => { if (e.target === e.currentTarget) pop(); }}
        >
          {top}
        </div>
      )}
    </DrillCtx.Provider>
  );
}

/** Port of modal(): head (title · count · close), scrollable body, foot.
 *  `search` is a parameter of the published helper that no caller on this page
 *  ever passes — there is no in-modal search here, so there is none here. */
export function DrillModal({ title, count, foot, children }: {
  title: string;
  count: string;
  foot: ReactNode;
  children: ReactNode;
}) {
  const { pop } = useDrill();
  return (
    <div className="modal">
      <div className="modal-head">
        <span className="modal-title">{title}</span>
        <span className="modal-count">{count}</span>
        <span className="spacer" style={{ flex: 1 }} />
        <button type="button" className="icon-btn" aria-label="Close" onClick={pop}>
          <svg viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M3.5 3.5l8 8M11.5 3.5l-8 8" />
          </svg>
        </button>
      </div>
      <div className="modal-body">{children}</div>
      <div className="modal-foot">{foot}</div>
    </div>
  );
}

export interface DrillCol { label: string; type?: 'num' | 'center'; title?: string }
export interface DrillRow { key: string; cls?: string; onClick?: () => void; cells: ReactNode[] }

/** A drill's own table — same alignment contract as the page's tables. */
export function DrillTable({ cols, rows }: { cols: DrillCol[]; rows: DrillRow[] }) {
  return (
    <div className="table-wrap"><table>
      <thead>
        <tr>
          {cols.map((c, i) => (
            <th
              key={c.label}
              {...(i === 0 ? {} : c.type ? { className: c.type } : {})}
              {...(c.title ? { title: c.title } : {})}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.key}
            {...(r.cls ? { className: r.cls } : {})}
            {...(r.onClick ? { onClick: r.onClick } : {})}
          >
            {r.cells.map((cell, i) => (
              // Column position is the identity: a table's cells are ordered
              // and never reordered, so the index is stable.
              <td key={cols[i]?.label ?? i} {...(i === 0 ? {} : cols[i]?.type ? { className: cols[i]?.type } : {})}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}

/**
 * The shared body of every drill: one modal, one table, the states in between.
 *
 * Loading and error states live INSIDE the modal rather than delaying it — a
 * click that appears to do nothing for a second reads as a broken control.
 */
export function DrillBody({ title, count, cols, rows, foot, loading, error, empty }: {
  title: string;
  count: string;
  cols: DrillCol[];
  rows: DrillRow[];
  foot: ReactNode;
  loading: boolean;
  error: string | null;
  /** The wording for "this drill has no rows", which is never the same
   *  sentence as an error and must never look like one. */
  empty: string | null;
}) {
  return (
    <DrillModal title={title} count={count} foot={foot}>
      {loading ? <div className="empty">Loading…</div>
        : error !== null ? <div className="empty">{error}</div>
        : empty !== null ? <div className="empty">{empty}</div>
        : <DrillTable cols={cols} rows={rows} />}
    </DrillModal>
  );
}
